/**
 * Everflow Conversion Tracking for Funnel Steps
 * Handles conversion events for specific funnel steps
 */

import {
  trackConversion,
  getEverflowTransactionIdFromCookie,
  getEverflowTransactionId,
  getEverflowParams,
  trackDepartmentMediaStartQuizConversion,
  trackDepartmentMediaQuizCompletedConversion,
  trackDepartmentMediaAddPaymentInfoConversion,
  trackDepartmentMediaPurchaseConversion,
  createEverflowTransactionForOffer,
  waitForEverflowOfferTransactionId,
  ensureEverflowTransactionId,
  isDirectCheckoutSelectionHit,
  UPSELL_EF_TRANSACTION_WAIT,
  UPSELL_EF_CONVERSION_SETTLE_MS,
  getAlternateOidValue,
} from './everflow'
import {
  trackEvent,
  trackGoogleAdsConversion,
  trackGtmStartQuiz,
  trackGtmQuizCompleted,
  trackGtmBeginCheckout,
  trackGtmPurchase,
} from './googleAnalytics'
import { trackAttentiveAddToCart } from './attentiveEcommerce'
import {
  trackMetaStartQuiz,
  trackMetaQuizCompleted,
  trackMetaFirstPurchase,
  trackMetaPurchase,
} from './metaTracking'
import {
  trackTikTokStartQuiz,
  trackTikTokQuizCompleted,
  trackTikTokAddPaymentInfo,
  trackTikTokAddPaymentInfoUpsell,
  trackTikTokFirstPurchase,
  trackTikTokPurchase,
} from './tiktokTracking'
import {
  trackWhopStartQuiz,
  trackWhopEmailPhoneCapture,
  trackWhopDQPage,
  trackWhopQuizCompleted,
  trackWhopAddPaymentInfo,
  trackWhopAddPaymentInfoUpsell,
  trackWhopProductSelection,
  trackWhopProductSelectionUpsell,
  trackWhopPlanSelection,
  trackWhopPlanSelectionUpsell,
  trackWhopPurchase,
  trackWhopPurchaseUpsell,
  rememberWhopUpsellProductName,
  rememberWhopUpsellAmount,
  rememberWhopUpsellPlan,
  resolveWhopUpsellProductName,
  resolveWhopUpsellAmount,
  resolveWhopUpsellPlan,
  rememberUpsellAmount,
  readUpsellAmount,
  rememberSelectedUpsellPlan,
  isStaleUpsellSessionWrite,
} from './whopTracking'
import { createMetaEventId } from './metaEventId'
import { config } from '../config/env'
import { logger } from './logger'
import { getExperimentId } from './experimentId'
import { sessionStore } from './sessionKey'
import { getFunnelIdFromPath } from './funnelUtils'

export { rememberUpsellAmount, rememberSelectedUpsellPlan } from './whopTracking'

const START_QUIZ_PIXELS_SESSION_KEY = 'start_quiz_ad_pixels_tracked'
const START_QUIZ_META_EVENT_ID_KEY = 'start_quiz_meta_event_id'

const getOrCreateStartQuizMetaEventId = (storedId) => {
  if (storedId) {
    sessionStore.setItem(START_QUIZ_META_EVENT_ID_KEY, storedId)
    return storedId
  }
  const existing = sessionStore.getItem(START_QUIZ_META_EVENT_ID_KEY)
  if (existing) return existing
  const id = createMetaEventId()
  sessionStore.setItem(START_QUIZ_META_EVENT_ID_KEY, id)
  return id
}

const fireStartQuizAdPixels = (funnelData, funnel, metaEventId) => {
  if (sessionStore.getItem(START_QUIZ_PIXELS_SESSION_KEY) === 'true') {
    logger.debug('ℹ️ Start Quiz Meta/TikTok/Whop already fired, skipping')
    return
  }

  const payload = { ...funnelData, funnel_id: funnel }
  trackMetaStartQuiz(payload, metaEventId)
  trackTikTokStartQuiz(payload, metaEventId)
  trackWhopStartQuiz(payload)
  trackGtmStartQuiz()
  sessionStore.setItem(START_QUIZ_PIXELS_SESSION_KEY, 'true')
}

const clearStartQuizTrackingSession = () => {
  sessionStore.removeItem('everflow_start_quiz_pending')
  sessionStore.removeItem('everflow_start_quiz_data')
  sessionStore.removeItem(START_QUIZ_PIXELS_SESSION_KEY)
  sessionStore.removeItem(START_QUIZ_META_EVENT_ID_KEY)
}

/**
 * Retry pending Start Quiz tracking if it failed on first page
 * Call this on subsequent pages to ensure tracking completes
 */
export const retryPendingStartQuizTracking = async () => {
  const pendingKey = 'everflow_start_quiz_pending'
  const dataKey = 'everflow_start_quiz_data'
  const trackedKey = 'everflow_start_quiz_tracked'
  
  // Check if there's a pending tracking
  if (sessionStore.getItem(pendingKey) !== 'true') {
    return
  }
  
  // Check if already tracked
  if (sessionStore.getItem(trackedKey) === 'true') {
    sessionStore.removeItem(pendingKey)
    sessionStore.removeItem(dataKey)
    return
  }
  
  // Get stored data
  const storedData = sessionStore.getItem(dataKey)
  if (!storedData) {
    logger.warn('⚠️ Pending Start Quiz tracking but no data found')
    sessionStore.removeItem(pendingKey)
    return
  }
  
  try {
    const data = JSON.parse(storedData)
    logger.debug('🔄 Retrying pending Start Quiz tracking from previous page')
    
    const metaEventId = getOrCreateStartQuizMetaEventId(data.metaEventId)
    fireStartQuizAdPixels({ funnel_id: data.funnel }, data.funnel, metaEventId)

    // Try to get transaction ID now
    let transactionId = getEverflowTransactionIdFromCookie() ||
                          (typeof window.EF !== 'undefined' ? getEverflowTransactionId() : null)

    // This event has already failed at least once for want of a transaction_id,
    // so recover one here instead of deferring to yet another page.
    if (!transactionId) {
      transactionId = await ensureEverflowTransactionId()
    }

    if (transactionId) {
      trackConversion({
        aid: 1,
        adv_event_id: 6,
        adv2: data.env,
        adv3: data.funnel,
        fbclid: data.fbclid,
        gclid: data.gclid,
        transaction_id: transactionId,
        adv5: metaEventId,
      })

      sessionStore.setItem(trackedKey, 'true')
      clearStartQuizTrackingSession()
      logger.debug('✅ Pending Start Quiz tracking completed with transaction_id:', transactionId)
    } else {
      sessionStore.setItem(dataKey, JSON.stringify({ ...data, metaEventId }))
      logger.debug('⏳ Transaction ID still not available, will retry Everflow on next page')
    }
  } catch (error) {
    logger.error('❌ Error retrying Start Quiz tracking:', error)
    sessionStore.removeItem(pendingKey)
    sessionStore.removeItem(dataKey)
  }
}

/**
 * Get the current environment (Live/Staging)
 * @returns {string} 'Live' or 'Staging'
 */
const getCurrentEnvironment = () => {
  return config.testMode ? 'Staging' : 'Live'
}

/** Merge path-derived funnel id for Meta CAPI custom_data.funnel_id */
const withMetaFunnelContext = (funnelData, funnel) => ({
  ...funnelData,
  funnel_id: funnelData.funnel_id || funnel,
})

/**
 * Get Everflow transaction ID from multiple sources
 * Priority: Cookies > Everflow API > funnelData (to avoid stale closures)
 *
 * @param {Object} funnelData - The funnel context data
 * @param {number|string|null} [offerId=null] - When provided, run a STRICT
 *   per-offer lookup that ONLY reads cookies for that offer_id (e.g. 3 for
 *   NAD on the CV-1 upsell repayment page). Used for cross-funnel scenarios
 *   where the URL path resolves to a different offer than the one we want
 *   to attribute. When omitted, behavior is unchanged (path-based).
 * @returns {string} Transaction ID or empty string
 */
const getTransactionId = (funnelData = {}, offerId = null) => {
  // STRICT per-offer lookup — used by callers like the NAD+ upsell page that
  // need to attribute conversions to a specific offer_id regardless of the
  // current URL path. Falling back to the path-based cookie here would
  // silently contaminate the conversion with the wrong funnel's transaction_id.
  if (offerId !== null && offerId !== undefined) {
    // Only read per-offer cookies — never funnelData.ef_tid here (it is often the
    // primary-checkout transaction for a different offer_id on upsell pages).
    const params = getEverflowParams(offerId)
    return (params && params.transaction_id) || ''
  }

  let transactionId = null
  
  // Try cookies FIRST (most reliable, always fresh)
  transactionId = getEverflowTransactionIdFromCookie()
  
  // Try Everflow API
  if (!transactionId && typeof window.EF !== 'undefined') {
    transactionId = getEverflowTransactionId()
  }
  
  // Try window.EF.getTransactionId()
  if (!transactionId && typeof window.EF !== 'undefined' && window.EF.getTransactionId) {
    transactionId = window.EF.getTransactionId()
  }
  
  // Fallback to funnelData (may be stale in closures)
  if (!transactionId) {
    transactionId = funnelData.ef_tid
  }
  
  return transactionId || ''
}

const ADD_PAYMENT_INFO_IN_FLIGHT = new Map()

const getAddPaymentInfoInFlightKey = (funnelData = {}) =>
  funnelData.email || funnelData.case_id || 'anonymous'

/**
 * Resolve the transaction_id a conversion should be attributed to.
 *
 * When the browser has none — a click that never landed, a cookie that was
 * dropped, a fresh browser on a resume link — `ensureEverflowTransactionId`
 * fires a recovery click rather than letting the event go out empty (Everflow
 * marks those Invalid). Callers should treat an empty result as "give up".
 *
 * @param {Object} funnelData
 * @param {number|string|null} [offerId]
 * @returns {Promise<string>}
 */
const resolveTransactionIdForConversion = async (funnelData = {}, offerId = null) => {
  const existingTransactionId = getTransactionId(funnelData, offerId)
  if (existingTransactionId) {
    return existingTransactionId
  }

  logger.debug('⏳ No Everflow transaction_id yet — waiting, then clicking if needed...')
  const recoveredId = await ensureEverflowTransactionId(offerId)
  return recoveredId || getTransactionId(funnelData, offerId)
}

/**
 * Get fbclid from URL
 * @returns {string|null}
 */
const getFbclid = () => {
  if (typeof window.EF !== 'undefined' && window.EF.urlParameter) {
    return window.EF.urlParameter('fbclid')
  }
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('fbclid')
}

/**
 * Get gclid from URL
 * @returns {string|null}
 */
const getGclid = () => {
  if (typeof window.EF !== 'undefined' && window.EF.urlParameter) {
    return window.EF.urlParameter('gclid')
  }
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('gclid')
}

/**
 * Detect if user is on mobile device (includes tablets)
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return false
  
  const ua = navigator.userAgent.toLowerCase()
  
  // Phone indicators
  const isPhone = /iphone|ipod|android.*mobile|windows phone|blackberry|bb10|opera mini|mobile|mobi/.test(ua)
  
  // Tablet indicators (includes Android tablets, iPad, generic 'tablet')
  const isTabletUA = /ipad|tablet/.test(ua) || /android(?!.*mobile)/.test(ua)
  
  // iPadOS 13+ reports Mac-like UA; detect by touch points on Mac platform
  const isIpadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
  
  // Fallback: touch + small screen (useful for some edge cases)
  const isSmallTouchScreen = (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) &&
                             Math.min(screen.width, screen.height) <= 1024

  return isPhone || isTabletUA || isIpadOS || isSmallTouchScreen
}

/**
 * Track Start Quiz conversion (current_height_weight_page)
 * Enhanced with better retry logic and persistent tracking
 * @param {Object} funnelData - The funnel context data
 * @param {Function} updateFunnelData - Function to update funnel data (optional)
 */
export const trackStartQuizConversion = (funnelData, updateFunnelData = null) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const homePageStyle = funnelData.home_page_style || 'default'
  const affid = funnelData.affid || ''
  const isMobile = isMobileDevice()
  
  let isCancelled = false
  let timeoutId = null
  let hasTracked = false
  let hasRequestedRecoveryClick = false

  /** Polls to spend assuming the click is merely slow before forcing a new one. */
  const RECOVERY_CLICK_AFTER_ATTEMPTS = 4
  
  // Check if already tracked in this session
  const sessionKey = 'everflow_start_quiz_tracked'
  if (sessionStore.getItem(sessionKey) === 'true') {
    logger.debug('ℹ️ Start Quiz already tracked in this session, skipping')
    return () => {} // Return empty cleanup
  }
  
  // Helper to attempt tracking with retries until transaction ID is available
  const attemptTracking = (attempt = 1, maxAttempts = 20) => {
    if (isCancelled) {
      logger.debug('🚫 Start Quiz tracking cancelled (component unmounted)')
      // Don't give up - store in sessionStorage to retry on next page
      if (!hasTracked) {
        sessionStore.setItem('everflow_start_quiz_pending', 'true')
        sessionStore.setItem('everflow_start_quiz_data', JSON.stringify({
          env, funnel, homePageStyle, affid, fbclid: getFbclid(), gclid: getGclid(),
          metaEventId: getOrCreateStartQuizMetaEventId()
        }))
        logger.debug('💾 Stored pending Start Quiz tracking for retry on next page')
      }
      return
    }
    
    const transactionId = getTransactionId(funnelData)
    const metaEventId = getOrCreateStartQuizMetaEventId()
    
    logger.debug(`📊 Tracking Start Quiz Conversion (attempt ${attempt}/${maxAttempts})`, { 
      env, 
      funnel, 
      homePageStyle, 
      affid, 
      transactionId: transactionId || 'NOT FOUND YET',
      checkingCookies: 'Checking cookies directly for fresh value',
      everflowReady: typeof window.EF !== 'undefined'
    })

    fireStartQuizAdPixels(funnelData, funnel, metaEventId)
    
    // If we have a transaction ID, track Everflow immediately
    if (transactionId) {
      trackConversion({
        aid: 1,
        adv_event_id: 6,
        adv2: env,
        adv3: funnel,
        adv4: 'source',
        adv5: metaEventId,
        fbclid: getFbclid(),
        gclid: getGclid(),
        transaction_id: transactionId,
      })

      hasTracked = true
      sessionStore.setItem(sessionKey, 'true')
      clearStartQuizTrackingSession()
      logger.debug('✅ Start Quiz Conversion tracked with transaction_id:', transactionId)
      return
    }
    
    // If no transaction ID yet and we haven't exceeded max attempts, retry
    if (attempt < maxAttempts) {
      // Past this point the click isn't just slow, it's missing. Ask for a
      // recovery click; the remaining retries pick up whatever it issues.
      if (attempt >= RECOVERY_CLICK_AFTER_ATTEMPTS && !hasRequestedRecoveryClick) {
        hasRequestedRecoveryClick = true
        ensureEverflowTransactionId()
      }

      // Use exponential backoff for first few attempts, then fixed 500ms
      const delay = attempt <= 3 ? 300 * attempt : 500
      logger.debug(`⏳ No transaction_id yet, retrying in ${delay}ms... (attempt ${attempt}/${maxAttempts})`)
      timeoutId = setTimeout(() => {
        attemptTracking(attempt + 1, maxAttempts)
      }, delay)
    } else {
      // Max attempts reached - DO NOT track without transaction_id
      // Instead, store for retry on next page
      logger.error('❌ Max attempts reached without transaction_id - storing for retry')
      sessionStore.setItem('everflow_start_quiz_pending', 'true')
      sessionStore.setItem('everflow_start_quiz_data', JSON.stringify({
        env, funnel, homePageStyle, affid, fbclid: getFbclid(), gclid: getGclid(),
        metaEventId: getOrCreateStartQuizMetaEventId()
      }))
    }
  }
  
  // Start the tracking attempt
  attemptTracking()

  // Department Media (aff_id2) EF-to-EF direct linking:
  // Fire an additional Start Quiz conversion into the Department Media advertiser account.
  // This is a no-op unless `aff_id2` is in the URL.
  trackDepartmentMediaStartQuizConversion()

  // After tracking conversion, try to capture ef_tid from cookies with multiple retries
  /*
  if (updateFunnelData && !funnelData.ef_tid) {
    const captureEfTid = (attempt = 1, maxAttempts = 5) => {
      setTimeout(() => {
        // Try to get from cookies first (functions already imported at top)
        let efTid = getEverflowTransactionIdFromCookie()
        
        // If not in cookies, try to get from Everflow API
        if (!efTid && typeof window.EF !== 'undefined') {
          efTid = getEverflowTransactionId()
        }
        
        // If not from URL either, try to get from EF object directly
        if (!efTid && typeof window.EF !== 'undefined' && window.EF.getTransactionId) {
          efTid = window.EF.getTransactionId()
        }
        
        if (efTid) {
          logger.debug(`🔑 [trackStartQuizConversion] Captured ef_tid after conversion (attempt ${attempt}):`, efTid)
          updateFunnelData('ef_tid', efTid)
        } else if (attempt < maxAttempts) {
          logger.debug(`⏳ [trackStartQuizConversion] ef_tid not found yet, retrying... (attempt ${attempt}/${maxAttempts})`)
          captureEfTid(attempt + 1, maxAttempts)
        } else {
          logger.warn('⚠️ [trackStartQuizConversion] Could not capture ef_tid after all attempts - this is expected in localhost/development')
          logger.debug('ℹ️ [trackStartQuizConversion] ef_tid will remain null until Everflow sets cookies (usually happens in production)')
        }
      }, attempt * 1000) // Exponential backoff: 1s, 2s, 3s, 4s, 5s
    }
    
    captureEfTid()
  }
  */

  // GA event: quiz_landing_source_[style] or quiz_landing_mobile_source_[style]
  const mobilePrefixLanding = isMobile ? 'quiz_landing_mobile_source_' : 'quiz_landing_source_'
  const landingEventName = mobilePrefixLanding + homePageStyle
  
  trackEvent(landingEventName, {
    source: homePageStyle,
    affid: affid,
    funnel_variant: funnel
  })

  // GA event: start_quiz_mobile or start_quiz_desktop
  const startQuizEventName = isMobile ? 'start_quiz_mobile' : 'start_quiz_desktop'
  
  trackEvent(startQuizEventName, {
    source: homePageStyle,
    affid: affid,
    funnel_variant: funnel
  })

  // Google Ads conversion: Start Quiz
  trackGoogleAdsConversion('iNr0CN2K3IscEIrejZJD')
  
  // Return cleanup function
  return () => {
    isCancelled = true
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Track Contact Info conversion - Email Captured (event 7)
 * @param {Object} funnelData - The funnel context data
 */
export const trackEmailCaptureConversion = async (funnelData) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')

  // Google Ads conversion: Capture Email — independent of Everflow, so it
  // should not wait on the transaction_id.
  trackGoogleAdsConversion('9c4yCLWwyYscEIrejZJD')

  const transactionId = await resolveTransactionIdForConversion(funnelData)
  if (!transactionId) {
    logger.error('❌ Cannot track Email Capture Conversion - no transaction_id even after a recovery click')
    return
  }

  logger.debug('📊 Tracking Email Capture Conversion', { env, funnel, transactionId })

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 7,
    adv2: env,
    adv3: funnel,
    transaction_id: transactionId
  })
}

/**
 * Track Contact Info conversion - Email & Phone Captured (event 8)
 * @param {Object} funnelData - The funnel context data
 */
export const trackEmailPhoneCaptureConversion = async (funnelData) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')

  // Ad-platform events don't depend on the Everflow transaction_id.
  trackWhopEmailPhoneCapture({ ...funnelData, funnel_id: funnel })
  trackGoogleAdsConversion('9c4yCLWwyYscEIrejZJD')

  const transactionId = await resolveTransactionIdForConversion(funnelData)
  if (!transactionId) {
    logger.error('❌ Cannot track Email & Phone Capture Conversion - no transaction_id even after a recovery click')
    return
  }

  logger.debug('📊 Tracking Email & Phone Capture Conversion', { env, funnel, transactionId })

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 8,
    adv2: env,
    adv3: funnel,
    transaction_id: transactionId
  })
}

/** sessionStorage — Quiz Completed Rejected; cleared when user leaves DQ page for another step */
export const DQ_PAGE_CONVERSION_SESSION_KEY = 'dq_page_conversion_tracked'

export const clearDQPageConversionTracked = () => {
  sessionStore.removeItem(DQ_PAGE_CONVERSION_SESSION_KEY)
}

/**
 * Track DQ Page conversion at most once per DQ page visit.
 * Refreshing the DQ page does not re-fire; returning from another funnel step does.
 *
 * @param {Object} funnelData - The funnel context data
 * @returns {boolean} Whether the conversion was tracked
 */
export const trackDQPageConversionOnce = (funnelData) => {
  if (sessionStore.getItem(DQ_PAGE_CONVERSION_SESSION_KEY) === 'true') {
    logger.debug('ℹ️ DQ page conversion already tracked this visit — skipping')
    return false
  }

  trackDQPageConversion(funnelData)
  sessionStore.setItem(DQ_PAGE_CONVERSION_SESSION_KEY, 'true')
  return true
}

/**
 * Track DQ Page conversion (disqualified user)
 * @param {Object} funnelData - The funnel context data
 */
export const trackDQPageConversion = async (funnelData) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''

  // Ad-platform events don't depend on the Everflow transaction_id.
  trackWhopDQPage({ ...funnelData, funnel_id: funnel })
  trackGoogleAdsConversion('VqB9CLq4yYscEIrejZJD')

  const transactionId = await resolveTransactionIdForConversion(funnelData)
  if (!transactionId) {
    logger.error('❌ Cannot track DQ Page Conversion - no transaction_id even after a recovery click')
    return
  }

  logger.debug('📊 Tracking DQ Page Conversion', { env, funnel, email, transactionId })

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 10,
    email: email,
    adv1: 'Rejected',
    adv2: env,
    adv3: funnel,
    transaction_id: transactionId
  })
}

/**
 * Track Checkout Page conversion (approved user) - Quiz Completed EF event
 * @param {Object} funnelData - The funnel context data
 */
export const trackCheckoutPageConversion = async (funnelData) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant

  // Shared with the Everflow payload below (adv5) so the two dedupe together
  // even though the Everflow call may be delayed by transaction_id recovery.
  const metaEventId = createMetaEventId()
  const quizCompletedPayload = withMetaFunnelContext(funnelData, funnel)

  trackMetaQuizCompleted(quizCompletedPayload, metaEventId)
  trackTikTokQuizCompleted(quizCompletedPayload, metaEventId)
  trackWhopQuizCompleted(quizCompletedPayload)

  // Department Media (aff_id2) Quiz Completed conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaQuizCompletedConversion()

  // Google Ads conversion: Quiz Complete
  trackGoogleAdsConversion('VqB9CLq4yYscEIrejZJD')

  trackGtmQuizCompleted(funnelData)

  // // Ad-platform events don't depend on the Everflow transaction_id — fire them
  // // now rather than making them wait on a recovery click.
  // trackMetaQuizCompleted(withMetaFunnelContext(funnelData, funnel), metaEventId)
  // trackTikTokQuizCompleted(withMetaFunnelContext(funnelData, funnel), metaEventId)
  // trackWhopQuizCompleted(withMetaFunnelContext(funnelData, funnel))

  // // Department Media (aff_id2) Quiz Completed conversion.
  // // No-op unless `aff_id2` is in the URL; session-deduped internally.
  // trackDepartmentMediaQuizCompletedConversion()

  // // Google Ads conversion: Quiz Complete
  // trackGoogleAdsConversion('VqB9CLq4yYscEIrejZJD')

  const transactionId = await resolveTransactionIdForConversion(funnelData)
  if (!transactionId) {
    logger.error('❌ Cannot track Checkout Page Conversion - no transaction_id even after a recovery click')
    return
  }

  logger.debug('📊 Tracking Checkout Page Conversion', { env, funnel, email, transactionId, experimentVariant })

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 1,
    email: email,
    adv1: 'Approved',
    adv2: env,
    adv3: funnel,
    adv5: metaEventId,
    transaction_id: transactionId,
  })
}

/** sessionStorage key — cleared on quiz start in CurrentHeightWeight */
export const CHECKOUT_PAGE_CONVERSION_SESSION_KEY = 'checkout_page_conversion_tracked'

/**
 * Set on NAD contact-info → checkout navigation (submit or browser forward from contact).
 * NadCheckoutPage fires Quiz Completed when this flag is present, then clears it.
 * Refreshing checkout does not set this flag, so Quiz Completed does not re-fire.
 */
export const NAD_CHECKOUT_QUIZ_COMPLETE_PENDING_KEY = 'nad_checkout_quiz_complete_pending'

/**
 * Set on Sermorelin contact-info → checkout navigation only.
 * SermorelinCheckoutPage fires Quiz Completed when this flag is present, then clears it.
 */
export const SERMORELIN_CHECKOUT_QUIZ_COMPLETE_PENDING_KEY = 'sermorelin_checkout_quiz_complete_pending'

/** sessionStorage — default product selection on NAD checkout, once per session until re-entry from contact */
export const NAD_CHECKOUT_DEFAULT_PRODUCT_SELECTION_KEY = 'nad_checkout_default_product_selection_tracked'

/** sessionStorage — default plan selection on NAD checkout, once per session until re-entry from contact */
export const NAD_CHECKOUT_DEFAULT_PLAN_SELECTION_KEY = 'nad_checkout_default_plan_selection_tracked'

/** sessionStorage — default product selection on Sermorelin checkout, once per session until re-entry from contact */
export const SERMORELIN_CHECKOUT_DEFAULT_PRODUCT_SELECTION_KEY =
  'sermorelin_checkout_default_product_selection_tracked'

/** sessionStorage — default plan selection on Sermorelin checkout, once per session until re-entry from contact */
export const SERMORELIN_CHECKOUT_DEFAULT_PLAN_SELECTION_KEY =
  'sermorelin_checkout_default_plan_selection_tracked'

/**
 * Track Checkout Page conversion (Quiz Complete) when the user lands on
 * `/checkout/selection`, at most once per checkout visit.
 *
 * - Never fires on `/checkout/payment` (remount, refresh, error return).
 * - Does not re-fire on selection page refresh (session flag stays set).
 * - Fires again after the user returns to a /cv-1 funnel step and re-enters
 *   selection (BaseForm clears CHECKOUT_PAGE_CONVERSION_SESSION_KEY off checkout).
 *
 * @param {Object} funnelData - The funnel context data
 * @returns {boolean} Whether the conversion was tracked
 */
export const trackCheckoutPageConversionOnce = (funnelData) => {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const isSelectionUrl = pathname.includes('/checkout/selection')

  if (!isSelectionUrl) {
    logger.debug('ℹ️ Not on checkout selection URL — skipping checkout page conversion', { pathname })
    return false
  }

  // Resume / email / SMS links land here without completing the quiz in this
  // page load. Quiz Completed is an in-flow event, so a direct hit must not
  // fire it (or mark the session tracked, which would suppress a later real quiz).
  if (isDirectCheckoutSelectionHit()) {
    logger.debug('ℹ️ Direct hit on checkout selection — skipping Quiz Completed conversion')
    return false
  }

  if (sessionStore.getItem(CHECKOUT_PAGE_CONVERSION_SESSION_KEY) === 'true') {
    logger.debug('ℹ️ Checkout page conversion already tracked this session — skipping')
    return false
  }

  logger.debug('📊 First visit to checkout selection this session — tracking checkout page conversion')
  trackCheckoutPageConversion(funnelData)
  sessionStore.setItem(CHECKOUT_PAGE_CONVERSION_SESSION_KEY, 'true')
  return true
}

/**
 * Track Payment Successful conversion (sync visit)
 * @param {Object} funnelData - The funnel context data
 * @param {Object} orderData - Order data (amount, customer_id)
 */
export const trackPaymentSuccessfulConversion = async (funnelData, orderData = {}) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const amount = orderData.amount || orderData.display_order_total || 0
  const customerId = orderData.customer_id || orderData.customerId || ''
  // const transactionId = getTransactionId(funnelData)

  const metaEventId = createMetaEventId()
  const metaFunnelData = { ...withMetaFunnelContext(funnelData, funnel), selected_product_price: amount }
  trackMetaFirstPurchase(metaFunnelData, metaEventId)
  trackGtmPurchase(metaFunnelData, { value: amount, transactionId: funnelData.case_id || customerId })
  trackMetaPurchase(metaFunnelData, metaEventId)
  trackTikTokFirstPurchase(metaFunnelData, metaEventId, customerId)
  trackTikTokPurchase(metaFunnelData, metaEventId, customerId)
  
  // CRITICAL: Validate transaction_id exists before tracking
  // Empty/missing transaction_id causes "Invalid" status in Everflow
  const transactionId = await resolveTransactionIdForConversion(funnelData)

  // Empty/missing transaction_id causes "Invalid" status in Everflow. The
  // resolver has already tried a recovery click, so there is nothing left.
  if (!transactionId) {
    logger.error('❌ Cannot track Payment Successful (Sync) - no transaction_id even after a recovery click')
    logger.error('This will cause Invalid status in Everflow. Aborting tracking.')
    logger.debug('Debug info:', {
      has_customer_id: !!customerId,
      has_email: !!email,
      amount,
      funnel,
      env
    })
    return
  }
  
  const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
  const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant

  logger.debug('📊 Tracking Payment Successful Conversion (Sync)', {
    env,
    funnel,
    email,
    amount,
    customerId,
    transactionId,
    selectedPaymentType,
    selectedPaymentMethod,
    experimentVariant
  })

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 4,
    amount: amount,
    email: email,
    adv1: 'sync_first',
    adv2: env,
    adv3: funnel,
    adv4: selectedPaymentMethod, // Specific payment method (card, afterpay_clearpay, klarna, etc.)
    adv5: metaEventId,
    fbclid: getFbclid(),
    gclid: getGclid(),
    transaction_id: transactionId,
    order_id: customerId,
  })

  // Department Media (aff_id2) Purchase conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaPurchaseConversion({ amount, orderId: customerId })

  // Google Ads conversion: Purchase
  trackGoogleAdsConversion('4kwaCKu_2YocEIrejZJD', {
    value: amount,
    currency: 'USD',
    transaction_id: customerId || transactionId
  })
}

/**
 * Track Payment Successful Async conversion (async visit)
 * @param {Object} funnelData - The funnel context data
 * @param {Object} orderData - Order data (amount, customer_id)
 */
export const trackPaymentSuccessfulAsyncConversion = async (funnelData, orderData = {}) => {
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const amount = orderData.amount || orderData.display_order_total || 0
  const customerId = orderData.customer_id || orderData.customerId || ''
  const transactionId = await resolveTransactionIdForConversion(funnelData)

  // Empty/missing transaction_id causes "Invalid" status in Everflow. The
  // resolver has already tried a recovery click, so there is nothing left.
  if (!transactionId) {
    logger.error('❌ Cannot track Payment Successful (Async) - no transaction_id even after a recovery click')
    logger.error('This will cause Invalid status in Everflow. Aborting tracking.')
    logger.debug('Debug info:', {
      has_customer_id: !!customerId,
      has_email: !!email,
      amount,
      funnel,
      env
    })
    // return
  }
  
  const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
  const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant

  logger.debug('📊 Tracking Payment Successful Conversion (Async)', {
    env,
    funnel,
    email,
    amount,
    customerId,
    transactionId,
    selectedPaymentType,
    selectedPaymentMethod,
    experimentVariant
  })

  const metaEventId = createMetaEventId()

  // Everflow conversion
  trackConversion({
    aid: 1,
    adv_event_id: 4,
    amount: amount,
    email: email,
    adv1: 'async_first',
    adv2: env,
    adv3: funnel,
    adv4: selectedPaymentMethod, // Specific payment method (card, afterpay_clearpay, klarna, etc.)
    adv5: metaEventId,
    fbclid: getFbclid(),
    gclid: getGclid(),
    order_id: customerId,
    transaction_id: transactionId,
  })

  const metaFunnelData = { ...withMetaFunnelContext(funnelData, funnel), selected_product_price: amount }
  trackMetaFirstPurchase(metaFunnelData, metaEventId)
  trackGtmPurchase(metaFunnelData, { value: amount, transactionId: funnelData.case_id || customerId })
  trackMetaPurchase(metaFunnelData, metaEventId)
  trackTikTokFirstPurchase(metaFunnelData, metaEventId, customerId)
  trackTikTokPurchase(metaFunnelData, metaEventId, customerId)

  // Department Media (aff_id2) Purchase conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaPurchaseConversion({ amount, orderId: customerId })

  // Google Ads conversion: Purchase
  trackGoogleAdsConversion('4kwaCKu_2YocEIrejZJD', {
    value: amount,
    currency: 'USD',
    transaction_id: customerId || transactionId
  })
}

/**
 * Track Add Payment Info conversion (checkout form submission) - Add Payment Info EF event (event_id: 9)
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly: reads transaction_id from the
 *   offer-specific cookie (`site_ef_transaction_id_offer_<offerId>`) and
 *   passes `offer_id` through to `EF.conversion()`. Used by cross-funnel
 *   upsells (e.g. CV-1's NAD+ upsell repayment page firing into offer 3).
 */
export const trackAddPaymentInfoConversion = (funnelData, options = {}) => {
  const inFlightKey = getAddPaymentInfoInFlightKey(funnelData)
  const existingInFlight = ADD_PAYMENT_INFO_IN_FLIGHT.get(inFlightKey)
  if (existingInFlight) {
    return existingInFlight
  }

  const promise = (async () => {
    const { offerId = null } = options
    const env = getCurrentEnvironment()
    const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
    const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
    const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
    const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant
    const transactionId = await resolveTransactionIdForConversion(funnelData, offerId)

    if (!transactionId) {
      logger.error('❌ Cannot track Add Payment Info Conversion - missing transaction_id')
      logger.error('This will cause Invalid status in Everflow. Aborting tracking.')
      return false
    }

    logger.debug('📊 Tracking Add Payment Info Conversion', {
      env,
      funnel,
      transactionId,
      selectedPaymentType,
      selectedPaymentMethod,
      experimentVariant,
      offerId
    })

    const conversionPayload = {
      aid: 1,
      adv_event_id: 9,
      adv2: env,
      adv3: funnel,
      adv4: selectedPaymentMethod,
      adv5: experimentVariant,
      transaction_id: transactionId
    }
    if (offerId !== null && offerId !== undefined) {
      conversionPayload.offer_id = offerId
    }
    trackConversion(conversionPayload)

    trackWhopAddPaymentInfo({ ...funnelData, funnel_id: funnel })
    trackTikTokAddPaymentInfo({ ...funnelData, funnel_id: funnel })
    trackDepartmentMediaAddPaymentInfoConversion()
    trackGoogleAdsConversion('hp-OCOnO04scEIrejZJD')
    trackGtmBeginCheckout({ ...funnelData, funnel_id: funnel })
    return true
  })().finally(() => {
    ADD_PAYMENT_INFO_IN_FLIGHT.delete(inFlightKey)
  })

  ADD_PAYMENT_INFO_IN_FLIGHT.set(inFlightKey, promise)
  return promise
}

/**
 * Track Add Payment Info Upsell conversion (checkout form submission) - Add Payment Info Upsell EF event (event_id: 45)
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly: reads transaction_id from the
 *   offer-specific cookie (`site_ef_transaction_id_offer_<offerId>`) and
 *   passes `offer_id` through to `EF.conversion()`. Used by cross-funnel
 *   upsells (e.g. CV-1's NAD+ upsell repayment page firing into offer 3).
 */
export const trackAddPaymentInfoUpsellConversion = (funnelData, options = {}) => {
  const { offerId = null, productName = null, amount: amountOption = null, planType = null } = options
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const transactionId = getTransactionId(funnelData, offerId)
  const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
  const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant
  if (amountOption != null) {
    rememberUpsellAmount(amountOption, offerId)
  }
  const upsellProductName = resolveWhopUpsellProductName(funnelData, { productName, offerId })
  const upsellAmount = readUpsellAmount(offerId) ?? resolveWhopUpsellAmount(funnelData, { amount: amountOption, offerId })
  const upsellPlan = resolveWhopUpsellPlan(funnelData, { planType, offerId })

  if (upsellAmount != null) {
    rememberWhopUpsellAmount(upsellAmount, offerId)
    rememberUpsellAmount(upsellAmount, offerId)
  }
  if (planType) {
    rememberWhopUpsellPlan(planType, offerId)
  }

  logger.debug('📊 Tracking Add Payment Info Upsell Conversion', {
    env,
    funnel,
    transactionId,
    selectedPaymentType,
    selectedPaymentMethod,
    experimentVariant,
    offerId,
    upsellProductName,
    upsellAmount,
    upsellPlan,
  })

  // Everflow conversion with payment method
  const conversionPayload = {
    aid: 1,
    adv_event_id: 14,
    adv2: env,
    adv3: funnel,
    adv4: selectedPaymentMethod, // Specific payment method (card, afterpay_clearpay, klarna, etc.)
    adv5: experimentVariant, // AB test variant (checkout_v1, checkout_v2)
    transaction_id: transactionId
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }
  trackConversion(conversionPayload)

  const addPaymentInfoUpsellData = {
    ...funnelData,
    funnel_id: funnel,
    selected_product_name: upsellProductName,
    selected_plan: upsellPlan || '',
    ...(upsellAmount != null
      ? {
          amount: upsellAmount,
          total_prescription_price: upsellAmount,
          selected_product_price: upsellAmount,
          display_order_total: upsellAmount,
          upsell_amount: upsellAmount,
        }
      : { upsell_amount: upsellAmount }),
  }
  trackWhopAddPaymentInfoUpsell(addPaymentInfoUpsellData)
  // Drop the main checkout medication so content_id follows the upsell product.
  trackTikTokAddPaymentInfoUpsell({
    ...addPaymentInfoUpsellData,
    selected_product_medication: '',
    selected_product_id: '',
    upsell_amount: upsellAmount,
    upsell_offer_id: offerId,
  })

  // Department Media (aff_id2) Add Payment Info conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaAddPaymentInfoConversion()

  // Google Ads conversion: Add Payment Info
  trackGoogleAdsConversion('hp-OCOnO04scEIrejZJD')

  // funnelData still carries the main checkout product id; report the upsell.
  trackGtmBeginCheckout(
    {
      ...addPaymentInfoUpsellData,
      selected_product_id: upsellProductName,
      product_select: '',
    },
    { value: upsellAmount }
  )
}

/**
 * Track Product Selection conversion
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {string} [options.productName] - Selected product name (defaults to
 *   `funnelData.selected_product_name`)
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly (cross-funnel upsells).
 *
 * `options.amount` is accepted by callers of this event but deliberately not
 * forwarded to Everflow — no revenue is attached to a product selection.
 */
export const trackProductSelectionConversion = async (funnelData, options = {}) => {
  const { offerId = null, productName = null } = options
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const selectedProductName = productName || funnelData.selected_product_name || 'unknown'
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant

  const isUpsellOffer = offerId !== null && offerId !== undefined
  let transactionId = getTransactionId(funnelData, offerId)
  if (isUpsellOffer && !transactionId) {
    logger.warn('⚠️ Cannot track upsell Product Selection — missing transaction_id', { offerId })
    return false
  }

  // Whop doesn't depend on the Everflow transaction_id.
  trackWhopProductSelection({
    ...funnelData,
    funnel_id: funnel,
    selected_product_name: selectedProductName,
  })

  if (!transactionId) {
    transactionId = await resolveTransactionIdForConversion(funnelData, offerId)
  }
  if (!transactionId) {
    logger.error('❌ Cannot track Product Selection Conversion - no transaction_id even after a recovery click')
    return false
  }

  logger.debug('📊 Tracking Product Selection Conversion', {
    env,
    funnel,
    email,
    transactionId,
    selectedProductName,
    experimentVariant,
    offerId
  })

  const conversionPayload = {
    aid: 1,
    adv_event_id: 12,
    email,
    adv2: env,
    adv3: funnel,
    adv4: selectedProductName,
    adv5: experimentVariant,
    transaction_id: transactionId
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }
  trackConversion(conversionPayload)
  return true
}

/**
 * Track Product Selection Upsell conversion
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {string} [options.productName] - Selected product name (defaults to
 *   `funnelData.selected_product_name`)
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly (cross-funnel upsells).
 * @param {string} [options.transactionId] - Explicit transaction_id from the
 *   upsell click (preferred over re-reading cookies immediately after EF.click()).
 */
export const trackProductSelectionUpsellConversion = (funnelData, options = {}) => {
  const {
    offerId = null,
    productName = null,
    transactionId: transactionIdOption = null,
    amount: amountOption = null,
    planType = null,
  } = options
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const transactionId = transactionIdOption || getTransactionId(funnelData, offerId)
  const selectedProductName = productName || funnelData.selected_product_name || 'unknown'
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant
  const rawUpsellAmount =
    amountOption ??
    funnelData.amount ??
    funnelData.total_prescription_price ??
    funnelData.selected_product_price ??
    null
  const upsellAmount =
    rawUpsellAmount == null || rawUpsellAmount === ''
      ? null
      : parseFloat(Number(rawUpsellAmount).toFixed(2))
  const upsellPlan = resolveWhopUpsellPlan(funnelData, { planType, offerId })

  const isUpsellOffer = offerId !== null && offerId !== undefined
  if (isUpsellOffer && !transactionId) {
    logger.warn('⚠️ Cannot track upsell Product Selection — missing transaction_id', { offerId })
    return false
  }

  logger.debug('📊 Tracking Product Selection Upsell Conversion', {
    env,
    funnel,
    email,
    transactionId,
    selectedProductName,
    experimentVariant,
    offerId,
    upsellAmount,
    upsellPlan,
  })

  const conversionPayload = {
    aid: 1,
    adv_event_id: 16,
    email,
    adv2: env,
    adv3: funnel,
    adv4: selectedProductName,
    adv5: experimentVariant,
    transaction_id: transactionId
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }
  trackConversion(conversionPayload)
  rememberWhopUpsellProductName(selectedProductName, offerId)
  const staleSessionWrite = isStaleUpsellSessionWrite(offerId, {
    planId: upsellPlan || planType,
    amount: upsellAmount,
  })
  if (!staleSessionWrite && upsellAmount != null) {
    rememberWhopUpsellAmount(upsellAmount, offerId)
  }
  if (!staleSessionWrite && upsellPlan) {
    rememberWhopUpsellPlan(upsellPlan, offerId)
  }
  trackWhopProductSelectionUpsell({
    ...funnelData,
    funnel_id: funnel,
    selected_product_name: selectedProductName,
    selected_plan: upsellPlan || '',
    ...(upsellAmount != null
      ? {
          amount: upsellAmount,
          total_prescription_price: upsellAmount,
          selected_product_price: upsellAmount,
        }
      : {}),
  })
  return true
}

/** sessionStorage key — default upsell product selection, once per offer per session */
export const getUpsellProductSelectionSessionKey = (offerId) =>
  `everflow_upsell_product_selection_offer_${offerId}`

/** sessionStorage key — default upsell plan on page load, once per offer per session */
export const getUpsellDefaultPlanSelectionSessionKey = (offerId) =>
  `everflow_upsell_default_plan_selection_offer_${offerId}`

/** True when the session-deduped upsell Plan Selection has already fired for this offer. */
export const hasUpsellDefaultPlanSelectionTracked = (offerId) => {
  if (offerId === null || offerId === undefined) return false
  return sessionStore.getItem(getUpsellDefaultPlanSelectionSessionKey(offerId)) === 'true'
}

/**
 * Track Product Selection at most once per session for a given upsell offer.
 * @returns {boolean} Whether the conversion was tracked
 */
export const trackProductSelectionConversionOnce = (funnelData, options = {}) => {
  const { offerId = null } = options
  if (offerId === null || offerId === undefined) {
    trackProductSelectionConversion(funnelData, options)
    return true
  }

  const key = getUpsellProductSelectionSessionKey(offerId)
  if (sessionStore.getItem(key) === 'true') {
    logger.debug('ℹ️ Upsell product selection already tracked this session — skipping', { offerId })
    return false
  }

  const tracked = trackProductSelectionUpsellConversion(funnelData, options)
  if (tracked) {
    sessionStore.setItem(key, 'true')
  }
  return tracked
}

/**
 * Track default Plan Selection at most once per session for a given upsell offer.
 * @returns {boolean} Whether the conversion was tracked
 */
export const trackPlanSelectionConversionOnce = (funnelData, options = {}) => {
  const { offerId = null } = options
  if (offerId === null || offerId === undefined) {
    trackPlanSelectionConversion(funnelData, options)
    return true
  }

  const key = getUpsellDefaultPlanSelectionSessionKey(offerId)
  if (sessionStore.getItem(key) === 'true') {
    logger.debug('ℹ️ Upsell default plan selection already tracked this session — skipping', { offerId })
    return false
  }

  const tracked = trackPlanSelectionUpsellConversion(funnelData, options)
  if (tracked) {
    sessionStore.setItem(key, 'true')
  }
  return tracked
}

/**
 * Create upsell offer click, wait for transaction_id, then fire default Product
 * Selection (session-deduped). If a plan is provided, fire Plan Selection after
 * the settle delay. Product can fire on page load before a plan is selected.
 */
export const runUpsellDefaultEverflowSelection = async ({
  offerId,
  product,
  plan,
  funnelData
}) => {
  if (!product) {
    return { productTracked: false, planTracked: false, transactionId: '' }
  }

  const monthlyAmount = product?.pricePlan?.monthly?.price ?? product?.price ?? null
  if (plan?.totalPrice != null) {
    rememberSelectedUpsellPlan(plan, offerId)
  } else {
    rememberUpsellAmount(monthlyAmount, offerId, { overwrite: false })
    rememberWhopUpsellAmount(monthlyAmount, offerId, { overwrite: false })
  }

  createEverflowTransactionForOffer(offerId)
  const transactionId = await waitForEverflowOfferTransactionId(offerId, UPSELL_EF_TRANSACTION_WAIT)

  if (!transactionId) {
    logger.warn(`⚠️ Upsell offer_id=${offerId}: no transaction_id before default selection events`)
  } else {
    // EF.click() cookie can appear before Everflow accepts follow-up events; plan
    // selection worked because it was delayed — apply the same settle wait first.
    await new Promise((resolve) => setTimeout(resolve, UPSELL_EF_CONVERSION_SETTLE_MS))
  }

  const productName = product.display_name || product.name
  const upsellAmount =
    plan?.totalPrice ??
    plan?.price ??
    product?.price ??
    null

  const basePayload = {
    ...funnelData,
    selected_product_id: product.id,
    product_select: product.id,
    selected_product_name: productName,
    ...(plan?.id ? { selected_plan: plan.id } : {}),
    // Override main-checkout prices so Whop value uses the upsell amount
    ...(upsellAmount != null
      ? {
          amount: upsellAmount,
          total_prescription_price: upsellAmount,
          selected_product_price: upsellAmount,
        }
      : {}),
  }

  const upsellConversionOptions = {
    productName,
    offerId,
    transactionId,
    ...(upsellAmount != null ? { amount: upsellAmount } : {}),
    ...(plan?.id ? { planType: plan.id } : {}),
  }

  const productTracked = trackProductSelectionConversionOnce(basePayload, upsellConversionOptions)

  let planTracked = false
  if (plan) {
    await new Promise((resolve) => setTimeout(resolve, UPSELL_EF_CONVERSION_SETTLE_MS))
    planTracked = trackPlanSelectionConversionOnce(
      {
        ...basePayload,
        selected_plan: plan.id,
        total_prescription_price: plan.totalPrice,
        selected_product_price: plan.totalPrice
      },
      { planType: plan.id, productName, offerId, transactionId }
    )
  }

  return { productTracked, planTracked, transactionId }
}

/**
 * Track Plan Selection conversion
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {string} [options.planType] - Selected plan (monthly, quarterly, etc.;
 *   defaults to `funnelData.selected_plan`)
 * @param {string} [options.productName] - Selected product name (defaults to
 *   `funnelData.selected_product_name`)
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly (cross-funnel upsells).
 */
export const trackPlanSelectionConversion = async (funnelData, options = {}) => {
  const { offerId = null, planType = null, productName = null, amount: amountOption = null, skipAttentiveAddToCart = false } = options
  const env = getCurrentEnvironment()

  // Select Plan on the quiz selection step. Upsells and NAD/Sermorelin's
  // default-on-load Everflow call pass a flag or an offer id and stay out.
  if (!skipAttentiveAddToCart && (offerId === null || offerId === undefined)) {
    trackAttentiveAddToCart(funnelData)
  }
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const selectedPlan = planType || funnelData.selected_plan || 'unknown'
  const productId = funnelData.selected_product_id || funnelData.product_select || ''
  const selectedProductName = productName || funnelData.selected_product_name || ''
  const selectedProductLabel = selectedProductName || 'unknown'
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant
  const amount = funnelData.total_prescription_price || funnelData.selected_product_price || 0

  const isUpsellOffer = offerId !== null && offerId !== undefined
  let transactionId = getTransactionId(funnelData, offerId)
  if (isUpsellOffer && !transactionId) {
    logger.warn('⚠️ Cannot track upsell Plan Selection — missing transaction_id', { offerId })
    return false
  }

  // Whop doesn't depend on the Everflow transaction_id.
  trackWhopPlanSelection({
    ...funnelData,
    funnel_id: funnel,
    selected_plan: selectedPlan,
    selected_product_name: selectedProductName || funnelData.selected_product_name,
  })

  if (!transactionId) {
    transactionId = await resolveTransactionIdForConversion(funnelData, offerId)
  }
  if (!transactionId) {
    logger.error('❌ Cannot track Plan Selection Conversion - no transaction_id even after a recovery click')
    return false
  }

  logger.debug('📊 Tracking Plan Selection Conversion', {
    env,
    funnel,
    email,
    transactionId,
    selectedPlan,
    productId,
    selectedProductName,
    selectedProductLabel,
    amount,
    experimentVariant,
    offerId
  })

  const conversionPayload = {
    aid: 1,
    adv_event_id: 13,
    email,
    adv2: env,
    adv3: funnel,
    adv4: selectedPlan,
    adv5: experimentVariant,
    transaction_id: transactionId
  }
  if (amount > 0) {
    conversionPayload.amount = amount
  }
  if (selectedProductLabel && selectedProductLabel !== 'unknown') {
    conversionPayload.adv1 = selectedProductLabel
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }
  trackConversion(conversionPayload)
  return true
}

/**
 * Track Plan Selection Upsell conversion
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} [options]
 * @param {string} [options.planType] - Selected plan (monthly, quarterly, etc.;
 *   defaults to `funnelData.selected_plan`)
 * @param {string} [options.productName] - Selected product name (defaults to
 *   `funnelData.selected_product_name`)
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly (cross-funnel upsells).
 * @param {string} [options.transactionId] - Explicit transaction_id from the
 *   upsell click (preferred over re-reading cookies immediately after EF.click()).
 */
export const trackPlanSelectionUpsellConversion = (funnelData, options = {}) => {
  const { offerId = null, planType = null, productName = null, transactionId: transactionIdOption = null } = options
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const transactionId = transactionIdOption || getTransactionId(funnelData, offerId)
  const selectedPlan = planType || funnelData.selected_plan || 'unknown'
  const productId = funnelData.selected_product_id || funnelData.product_select || ''
  const selectedProductName = productName || funnelData.selected_product_name || ''
  const selectedProductLabel = selectedProductName || 'unknown'
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant
  const amount = funnelData.total_prescription_price || funnelData.selected_product_price || 0
  const staleSessionWrite = isStaleUpsellSessionWrite(offerId, {
    planId: selectedPlan,
    amount,
  })
  if (!staleSessionWrite && amount > 0) {
    rememberUpsellAmount(amount, offerId)
  }

  const isUpsellOffer = offerId !== null && offerId !== undefined
  if (isUpsellOffer && !transactionId) {
    logger.warn('⚠️ Cannot track upsell Plan Selection — missing transaction_id', { offerId })
    return false
  }

  logger.debug('📊 Tracking Plan Selection Upsell Conversion', {
    env,
    funnel,
    email,
    transactionId,
    selectedPlan,
    productId,
    selectedProductName,
    selectedProductLabel,
    amount,
    experimentVariant,
    offerId
  })

  const conversionPayload = {
    aid: 1,
    adv_event_id: 17,
    email,
    adv2: env,
    adv3: funnel,
    adv4: selectedPlan,
    adv5: experimentVariant,
    transaction_id: transactionId
  }
  if (amount > 0) {
    conversionPayload.amount = amount
  }
  if (selectedProductLabel && selectedProductLabel !== 'unknown') {
    conversionPayload.adv1 = selectedProductLabel
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }
  trackConversion(conversionPayload)
  rememberWhopUpsellProductName(selectedProductLabel, offerId)
  if (!staleSessionWrite && amount > 0) {
    rememberWhopUpsellAmount(amount, offerId)
  }
  if (!staleSessionWrite && selectedPlan && selectedPlan !== 'unknown') {
    rememberWhopUpsellPlan(selectedPlan, offerId)
  }
  const upsellProductName = resolveWhopUpsellProductName(funnelData, {
    productName: selectedProductLabel !== 'unknown' ? selectedProductLabel : null,
    offerId,
  })
  const upsellAmount = resolveWhopUpsellAmount(funnelData, {
    amount: amount > 0 ? amount : null,
    offerId,
  })
  trackWhopPlanSelectionUpsell({
    ...funnelData,
    funnel_id: funnel,
    selected_product_name: upsellProductName,
    selected_plan: selectedPlan !== 'unknown' ? selectedPlan : '',
    ...(upsellAmount != null
      ? {
          amount: upsellAmount,
          total_prescription_price: upsellAmount,
          selected_product_price: upsellAmount,
        }
      : {}),
  })
  return true
}

/**
 * Calculate discounted price from promo data
 * @param {number} originalPrice - Original product price
 * @returns {number} Discounted price
 */
const calculateDiscountedPrice = (originalPrice) => {
  // Get promo data from sessionStorage (same as cvProductCatalog.js)
  const percentOff = sessionStore.getItem('cv_percent_off') || ''
  const amountOff = sessionStore.getItem('cv_amount_off') || ''
  
  let discountedPrice = originalPrice
  
  logger.debug('🔍 calculateDiscountedPrice called:', {
    originalPrice,
    percentOff,
    amountOff,
    percentOffTrimmed: percentOff.trim(),
    percentOffParsed: parseFloat(percentOff),
    isPercentValid: percentOff.trim() !== '' && !isNaN(parseFloat(percentOff))
  })
  
  // Check percent discount first
  if (percentOff.trim() !== '' && !isNaN(parseFloat(percentOff))) {
    const percent = parseFloat(percentOff)
    discountedPrice = originalPrice - (originalPrice * percent / 100)
    // Round to 2 decimal places
    discountedPrice = parseFloat(discountedPrice.toFixed(2))
    logger.debug('✅ Applied percent discount:', percent + '%', '→', discountedPrice)
  } 
  // Then check amount discount
  else if (amountOff.trim() !== '' && !isNaN(parseFloat(amountOff))) {
    const amount = parseFloat(amountOff)
    discountedPrice = Math.max(0, originalPrice - amount)
    // Round to 2 decimal places
    discountedPrice = parseFloat(discountedPrice.toFixed(2))
    logger.debug('✅ Applied amount discount: $' + amount, '→', discountedPrice)
  } else {
    logger.debug('ℹ️ No valid promo found, using original price')
  }
  
  return discountedPrice
}

/**
 * Track CareValidate Payment Successful conversion - First Purchase EF event (event_id: 4)
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} caseData - CareValidate case data (caseId, amount)
 * @param {Object} [options]
 * @param {number|string} [options.offerId] - Default offer_id for this conversion
 *   (e.g. 3 for NAD, 4 for Sermorelin). Overridden when the session landed with
 *   `oid=23` (CV-1/CV-2) or `oid=54` (NAD/Sermorelin) in the URL or Everflow
 *   persisted params — those alternate tracker sessions must attribute to offer
 *   23 or 54. Reads transaction_id from the offer-specific cookie
 *   (`site_ef_transaction_id_offer_<offerId>`) and passes `offer_id` through to
 *   `EF.conversion()`.
 * @param {boolean} [options.isUpsell=false] - When true, fires Meta Purchase
 *   only (no First Purchase). Set for upsell products.
 */
export const trackCvPaymentSuccessfulConversion = async (funnelData, caseData = {}, options = {}) => {
  const { offerId: optionsOfferId = null, isUpsell = false } = options
  // Alternate Everflow tracker sessions (oid=23 on CV-1/CV-2, oid=54 on NAD/Sermorelin)
  // must use that offer_id for transaction lookup and conversion payload — not the
  // funnel default passed in options (e.g. NAD's offerId: 3).
  const alternateOid = getAlternateOidValue()
  const offerId = alternateOid ?? optionsOfferId
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const transactionId = await resolveTransactionIdForConversion(funnelData, offerId)

  // Empty/missing transaction_id causes "Invalid" status in Everflow
  if (!transactionId) {
    logger.error('❌ Cannot track CV Payment conversion - missing transaction_id')
    logger.error('This will cause Invalid status in Everflow. Aborting tracking.')
    logger.debug('Debug info:', {
      has_case_id: !!caseData.caseId,
      has_email: !!email,
      funnel,
      env,
      offerId
    })
    // return false
  }
  
  const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
  const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant
  
  // Calculate discounted price for Everflow
  // Strategy: If promo exists in sessionStorage, ALWAYS calculate from original price
  // Otherwise use the stored display values
  const percentOff = sessionStore.getItem('cv_percent_off') || ''
  const amountOff = sessionStore.getItem('cv_amount_off') || ''
  
  logger.debug('🔍 Promo check:', {
    percentOff,
    amountOff,
    percentOffTrimmed: percentOff.trim(),
    percentOffValid: percentOff.trim() !== '' && !isNaN(parseFloat(percentOff)),
    amountOffTrimmed: amountOff.trim(),
    amountOffValid: amountOff.trim() !== '' && !isNaN(parseFloat(amountOff))
  })
  
  const hasPromo = (percentOff.trim() !== '' && !isNaN(parseFloat(percentOff))) || 
                   (amountOff.trim() !== '' && !isNaN(parseFloat(amountOff)))
  
  logger.debug('🎟️ hasPromo:', hasPromo, 'selected_product_price:', funnelData.selected_product_price)
  
  let discountedAmount = 0
  
  // PREFERRED: Use total_prescription_price if available (both funnels calculate this)
  // This field is calculated in both variant-cv-1 and variant-cv-nad as:
  //   discountedTotal = hasPromo ? Math.max(0, totalPrice - promoDiscount) : totalPrice
  //   total_prescription_price = Math.round(discountedTotal)
  // So it ALREADY includes any promo discount and represents the actual bundle price.
  if (funnelData.total_prescription_price) {
    discountedAmount = funnelData.total_prescription_price
    logger.debug('💰 Using total_prescription_price (bundle price, promo already applied if any):', discountedAmount)
  } 
  // FALLBACK: If total_prescription_price not available and promo exists, calculate discount
  else if (hasPromo && funnelData.selected_product_price) {
    logger.debug('🔄 Calling calculateDiscountedPrice (fallback)...')
    discountedAmount = calculateDiscountedPrice(funnelData.selected_product_price)
    logger.debug('💰 Promo detected - calculated discount:', discountedAmount, 'from original:', funnelData.selected_product_price)
  } 
  // FALLBACK: No promo and no total_prescription_price - use stored display values
  else {
    logger.debug('ℹ️ No total_prescription_price, using fallback stored values')
    // Try to use stored display values
    if (funnelData.display_order_total && typeof funnelData.display_order_total === 'string') {
      const parsed = parseFloat(funnelData.display_order_total.replace(/[$,]/g, ''))
      if (!isNaN(parsed) && parsed > 0) {
        discountedAmount = parsed
        logger.debug('📊 Using display_order_total:', discountedAmount)
      }
    }
    
    if (discountedAmount === 0 && funnelData.chosen_medicine_price && typeof funnelData.chosen_medicine_price === 'string') {
      const parsed = parseFloat(funnelData.chosen_medicine_price.replace(/[$,]/g, ''))
      if (!isNaN(parsed) && parsed > 0) {
        discountedAmount = parsed
        logger.debug('📊 Using chosen_medicine_price:', discountedAmount)
      }
    }
    
    if (discountedAmount === 0 && funnelData.selected_product_price) {
      discountedAmount = funnelData.selected_product_price
      logger.debug('📊 Using selected_product_price:', discountedAmount)
    }
  }
  
  const rawAmount = caseData.amount || discountedAmount || funnelData.selected_product_price || 0
  // Round to 2 decimal places
  const amount = parseFloat(rawAmount.toFixed(2))
  const caseId = caseData.caseId || funnelData.case_id || ''

  logger.debug('📊 Tracking CareValidate Payment Successful Conversion', { 
    env, 
    funnel, 
    email, 
    amount, 
    caseId,
    transactionId,
    selectedPaymentType,
    selectedPaymentMethod,
    experimentVariant,
    display_order_total: funnelData.display_order_total,
    chosen_medicine_price: funnelData.chosen_medicine_price,
    selected_product_price: funnelData.selected_product_price,
    calculated_discount: discountedAmount,
    promo_amount_off: sessionStore.getItem('cv_amount_off'),
    promo_percent_off: sessionStore.getItem('cv_percent_off')
  })

  const metaEventId = createMetaEventId()

  // Everflow conversion - Event ID: 4 (same as payment success)
  const conversionPayload = {
    aid: 1,
    adv_event_id: 4,
    amount: amount, // Discounted price (rounded to 2 decimals)
    email: email,
    adv1: 'async_first', // All CareValidate submissions are async
    adv2: env,
    adv3: funnel,
    adv4: selectedPaymentMethod, // Specific payment method (card, afterpay_clearpay, klarna, etc.)
    adv5: metaEventId,
    fbclid: getFbclid(),
    gclid: getGclid(),
    order_id: caseId,
    transaction_id: transactionId
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }

  trackConversion(conversionPayload)

  const metaFunnelData = { ...withMetaFunnelContext(funnelData, funnel), selected_product_price: amount }
  if (!isUpsell) {
    trackMetaFirstPurchase(metaFunnelData, metaEventId)
    trackGtmPurchase(metaFunnelData, { value: amount, transactionId: caseId })
    trackTikTokFirstPurchase(metaFunnelData, metaEventId, caseId)
  }
  trackMetaPurchase(metaFunnelData, metaEventId)
  trackTikTokPurchase(metaFunnelData, metaEventId, caseId)
  trackWhopPurchase({ ...metaFunnelData, case_id: caseId })

  // Department Media (aff_id2) Purchase conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaPurchaseConversion({ amount, orderId: caseId })

  // Google Ads conversion: Purchase
  trackGoogleAdsConversion('4kwaCKu_2YocEIrejZJD', {
    value: amount,
    currency: 'USD',
    transaction_id: caseId || transactionId
  })
  return true
}

/**
 * Track CareValidate Payment Successful Upsell conversion - First Purchase Upsell EF event (event_id: 46)
 *
 * @param {Object} funnelData - The funnel context data
 * @param {Object} caseData - CareValidate case data (caseId, amount)
 * @param {Object} [options]
 * @param {number|string} [options.offerId] - When provided, attributes the
 *   conversion to this offer_id explicitly: reads transaction_id from the
 *   offer-specific cookie (`site_ef_transaction_id_offer_<offerId>`) and
 *   passes `offer_id` through to `EF.conversion()`. Used by cross-funnel
 *   upsells (e.g. CV-1's NAD+ upsell repayment page firing into offer 3).
 */
export const trackCvPaymentSuccessfulUpsellConversion = (funnelData, caseData = {}, options = {}) => {
  const { offerId = null, productName = null } = options
  const env = getCurrentEnvironment()
  const funnel = funnelData.funnel_id || getFunnelIdFromPath(typeof window !== 'undefined' ? window.location.pathname : '')
  const email = funnelData.email || ''
  const transactionId = getTransactionId(funnelData, offerId)
  
  const selectedPaymentType = funnelData.selected_payment_type || 'unknown'
  const selectedPaymentMethod = funnelData.selected_payment_method || selectedPaymentType
  const experimentVariant = getExperimentId() || config.defaultCheckoutExperimentVariant // Get current experiment variant
  
  // Calculate discounted price for Everflow
  // Strategy: If promo exists in sessionStorage, ALWAYS calculate from original price
  // Otherwise use the stored display values
  const percentOff = sessionStore.getItem('cv_percent_off') || ''
  const amountOff = sessionStore.getItem('cv_amount_off') || ''
  const hasPromo = (percentOff.trim() !== '' && !isNaN(parseFloat(percentOff))) || 
                   (amountOff.trim() !== '' && !isNaN(parseFloat(amountOff)))
  
  let discountedAmount = 0
  
  // PREFERRED: Use total_prescription_price if available (both funnels calculate this)
  // This field is calculated in both variant-cv-1 and variant-cv-nad as:
  //   discountedTotal = hasPromo ? Math.max(0, totalPrice - promoDiscount) : totalPrice
  //   total_prescription_price = Math.round(discountedTotal)
  // So it ALREADY includes any promo discount and represents the actual bundle price.
  if (funnelData.total_prescription_price) {
    discountedAmount = funnelData.total_prescription_price
    logger.debug('💰 Using total_prescription_price (bundle price, promo already applied if any):', discountedAmount)
  } 
  // FALLBACK: If total_prescription_price not available and promo exists, calculate discount
  else if (hasPromo && funnelData.selected_product_price) {
    logger.debug('🔄 Calling calculateDiscountedPrice (fallback)...')
    discountedAmount = calculateDiscountedPrice(funnelData.selected_product_price)
    logger.debug('💰 Promo detected - calculated discount:', discountedAmount, 'from original:', funnelData.selected_product_price)
  } 
  // FALLBACK: No promo and no total_prescription_price - use stored display values
  else {
    logger.debug('ℹ️ No total_prescription_price, using fallback stored values')
    // Try to use stored display values
    if (funnelData.display_order_total && typeof funnelData.display_order_total === 'string') {
      const parsed = parseFloat(funnelData.display_order_total.replace(/[$,]/g, ''))
      if (!isNaN(parsed) && parsed > 0) {
        discountedAmount = parsed
        logger.debug('📊 Using display_order_total:', discountedAmount)
      }
    }
    
    if (discountedAmount === 0 && funnelData.chosen_medicine_price && typeof funnelData.chosen_medicine_price === 'string') {
      const parsed = parseFloat(funnelData.chosen_medicine_price.replace(/[$,]/g, ''))
      if (!isNaN(parsed) && parsed > 0) {
        discountedAmount = parsed
        logger.debug('📊 Using chosen_medicine_price:', discountedAmount)
      }
    }
    
    if (discountedAmount === 0 && funnelData.selected_product_price) {
      discountedAmount = funnelData.selected_product_price
      logger.debug('📊 Using selected_product_price:', discountedAmount)
    }
  }
  
  const rawAmount = caseData.amount || discountedAmount || funnelData.selected_product_price || 0
  // Round to 2 decimal places
  const amount = parseFloat(rawAmount.toFixed(2))
  const caseId = caseData.caseId || funnelData.case_id || ''

  // Upsell product/plan/price live in session (and options), not funnelData —
  // funnelData still holds the main checkout selection. Override so GTM /
  // Meta / TikTok / Whop report the upsell, not the main product.
  const upsellProductName = resolveWhopUpsellProductName(funnelData, { productName, offerId })
  const upsellPlan = resolveWhopUpsellPlan(funnelData, { offerId })

  // GTM doesn't depend on the Everflow transaction_id. The upsell is added to
  // the main purchase's case, so suffix the offer id — GA4 drops purchases
  // whose transaction_id repeats.
  trackGtmPurchase(
    {
      ...withMetaFunnelContext(funnelData, funnel),
      selected_product_id: upsellProductName,
      product_select: '',
      selected_product_name: upsellProductName,
      selected_plan: upsellPlan || '',
    },
    {
      value: amount,
      transactionId: caseId ? `${caseId}-upsell-${offerId ?? 'unknown'}` : '',
    }
  )

  // CRITICAL: Validate transaction_id exists before tracking
  // Empty/missing transaction_id causes "Invalid" status in Everflow
  if (!transactionId) {
    return
  }

  // logger.debug('📊 Tracking CareValidate Payment Successful Conversion', { 
  //   env, 
  //   funnel, 
  //   email, 
  //   amount, 
  //   caseId,
  //   transactionId,
  //   selectedPaymentType,
  //   selectedPaymentMethod,
  //   experimentVariant,
  //   display_order_total: funnelData.display_order_total,
  //   chosen_medicine_price: funnelData.chosen_medicine_price,
  //   selected_product_price: funnelData.selected_product_price,
  //   calculated_discount: discountedAmount,
  //   promo_amount_off: sessionStore.getItem('cv_amount_off'),
  //   promo_percent_off: sessionStore.getItem('cv_percent_off')
  // })

  // Everflow conversion - Event ID: 4 (same as payment success)
  const conversionPayload = {
    aid: 1,
    adv_event_id: 15,
    amount: amount, // Discounted price (rounded to 2 decimals)
    email: email,
    adv1: 'async_first_upsell', // All CareValidate submissions are async
    adv2: env,
    adv3: funnel,
    adv4: selectedPaymentMethod, // Specific payment method (card, afterpay_clearpay, klarna, etc.)
    fbclid: getFbclid(),
    gclid: getGclid(),
    order_id: caseId,
    transaction_id: transactionId
  }
  if (offerId !== null && offerId !== undefined) {
    conversionPayload.offer_id = offerId
  }

  const metaEventId = createMetaEventId()
  conversionPayload.adv5 = metaEventId

  trackConversion(conversionPayload)

  const metaFunnelData = { ...withMetaFunnelContext(funnelData, funnel), selected_product_price: amount }

  const upsellTrackingData = {
    ...metaFunnelData,
    case_id: caseId,
    selected_product_name: upsellProductName,
    selected_product_id: upsellProductName,
    // Clear main-checkout medication/type so they aren't attributed to the upsell
    // (also lets the TikTok slug resolve from the upsell name)
    selected_product_medication: '',
    selected_product_type: '',
    selected_plan: upsellPlan || '',
    amount,
    total_prescription_price: amount,
    selected_product_price: amount,
    display_order_total: amount,
  }
  trackMetaPurchase(upsellTrackingData, metaEventId)
  trackTikTokPurchase(upsellTrackingData, metaEventId, caseId)
  trackWhopPurchaseUpsell(upsellTrackingData)

  // Department Media (aff_id2) Purchase conversion.
  // No-op unless `aff_id2` is in the URL; session-deduped internally.
  trackDepartmentMediaPurchaseConversion({ amount, orderId: caseId })

  // Google Ads conversion: Purchase
  trackGoogleAdsConversion('4kwaCKu_2YocEIrejZJD', {
    value: amount,
    currency: 'USD',
    transaction_id: caseId || transactionId
  })
}
